import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "react-oidc-context";

import { CognitoIdpError } from "@/auth/cognitoIdp";
import { getLoginHint, setLoginHint } from "@/auth/loginHint";
import { authenticateWithPasskey, passkeysSupported } from "@/auth/passkeys";
import { beginPasskeySession } from "@/auth/passkeySession";
import { Field, Input } from "@/components/form";
import { Spinner } from "@/components/Spinner";
import { Button, Notice } from "@/components/ui";

// Seule l'adresse email est retenue, jamais un jeton.
const EMAIL_KEY = "ps.cp.lastEmail";

function readRememberedEmail(): string {
  try {
    return window.localStorage.getItem(EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberEmail(email: string): void {
  try {
    window.localStorage.setItem(EMAIL_KEY, email);
  } catch {
    // Stockage indisponible : on ne retient rien.
  }
}

function describeSignInError(err: unknown): string {
  if (err instanceof Error && (err.name === "NotAllowedError" || err.name === "AbortError")) {
    return "Touch ID was cancelled or timed out. Try again, or use your password.";
  }
  if (err instanceof Error && err.name === "SecurityError") {
    return "Touch ID is not available at this address. Use your password instead.";
  }
  if (err instanceof CognitoIdpError && err.code === "NotAuthorizedException") {
    return "Touch ID sign-in was refused for this email. Use your password instead.";
  }
  if (err instanceof Error) return err.message;
  return "Sign-in failed. Use your password instead.";
}

export function LoginScreen() {
  const auth = useAuth();
  const [email, setEmail] = useState(readRememberedEmail);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const supported = passkeysSupported();

  // Rechargement après une connexion par mot de passe : même rebond silencieux
  // par le Hosted UI qu'avant l'existence de cet écran (son cookie de session
  // rouvre la GUI sans rien saisir). Voir auth/loginHint.ts.
  const autoPassword = getLoginHint() === "password";
  useEffect(() => {
    if (autoPassword) void auth.signinRedirect();
  }, []);

  if (autoPassword) return <Spinner label="Redirecting to sign-in…" />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const result = await authenticateWithPasskey(email);
      rememberEmail(email.trim());
      setLoginHint("passkey");
      await beginPasskeySession(result);
      // Succès : react-oidc-context passe à `isAuthenticated`, cet écran se démonte.
    } catch (err) {
      console.error("Touch ID sign-in failed", err);
      setMessage(describeSignInError(err));
      setBusy(false);
    }
  }

  function continueWithPassword() {
    setLoginHint("password");
    void auth.signinRedirect();
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <form className="card max-w-md w-full space-y-4" onSubmit={onSubmit}>
        <div className="flex items-center gap-2">
          <img src="/hapsout-mark-dark.png" alt="" className="h-8 w-8" width={32} height={32} />
          <h1 className="text-xl font-semibold text-ink">PulseStation · Control Plane</h1>
        </div>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {message && (
          <Notice tone="danger" role="alert">
            {message}
          </Notice>
        )}
        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={busy}
          disabled={!supported || email.trim() === ""}
        >
          {busy ? "Waiting for Touch ID…" : "Sign in with Touch ID"}
        </Button>
        {!supported && (
          <p className="text-sm text-ink-muted">This browser does not support Touch ID sign-in.</p>
        )}
        <Button variant="secondary" className="w-full" onClick={continueWithPassword}>
          Use password instead
        </Button>
      </form>
    </div>
  );
}
